import Select, { components, OptionProps } from "react-select"
import { CSSProperties } from "react"
import { SelectOption } from "./select-input"

// "Calgary" for one value, "Calgary +2" for more
const summarize = (value: SelectOption[]) =>
  value.length === 1 ? value[0].label : `${value[0].label} +${value.length - 1}`

const CheckboxOption = (props: OptionProps<SelectOption, true>) => (
  <components.Option {...props}>
    <input type="checkbox" className="form-check-input me-2" checked={props.isSelected} readOnly tabIndex={-1} />
    {props.label}
  </components.Option>
)

interface MultiSelectProps {
  label: string
  options: SelectOption[]
  value: SelectOption[]
  onChange: (value: SelectOption[]) => void
  error?: boolean
  message?: string
  name?: string
  placeholder?: string
  className?: string
  containerStyle?: CSSProperties
  disabled?: boolean
  onMenuOpen?: () => void
  onMenuClose?: () => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  // Renders the menu on top of the page so it isn't clipped by a modal or scroll container.
  menuPortal?: boolean
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  value,
  onChange,
  error,
  message,
  name,
  placeholder,
  className,
  containerStyle,
  disabled,
  onMenuOpen,
  onMenuClose,
  onKeyDown,
  onBlur,
  menuPortal
}) => {
  return (
    <div
      style={containerStyle}
      title={value.map(v => v.label).join(", ") || undefined}
      className={`input-container ${error ? "error" : ""} ${className ?? ""}`}
    >
      {label && <span className="input-label">{label}</span>}

      <Select
        name={name}
        className="input p-0"
        // One line only: selected values are summarised as text ("Calgary" / "Calgary +2"),
        // never as rows of chips; options stay in the menu with a checkbox.
        placeholder={value.length ? summarize(value) : placeholder}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        components={{ Option: CheckboxOption }}
        isDisabled={disabled}
        options={options}
        value={value}
        onChange={(val) => onChange((val ?? []) as SelectOption[])}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        isMulti
        isSearchable
        closeMenuOnSelect={false}
        menuPortalTarget={menuPortal ? document.body : undefined}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (base) => ({
            ...base,
            backgroundColor: "var(--bs-body-bg)",
            border: "none",
            flexWrap: "nowrap",
          }),
          valueContainer: (base) => ({
            ...base,
            flexWrap: "nowrap",
            overflow: "hidden",
          }),
          placeholder: (base) => ({
            ...base,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: value.length ? "var(--bs-body-color)" : base.color,
          }),
          menu: (base) => ({
            ...base,
            backgroundColor: "var(--bs-body-bg)",
            zIndex: 9999,
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? "var(--bs-secondary-bg)" : "var(--bs-body-bg)",
            color: "var(--bs-body-color)",
          }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: "var(--bs-secondary-bg)",
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: "var(--bs-body-color)",
          }),
          multiValueRemove: (base) => ({
            ...base,
            cursor: "pointer",
          }),
          input: (base) => ({
            ...base,
            color: "var(--bs-body-color)",
            padding: "0px",
          }),
        }}
      />

      {message && <span className="input-message">{message}</span>}
    </div>
  )
}

export default MultiSelect
